import { Fragment, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { z } from 'zod'

import { InputSearch } from '@/components/ui/input/input-search'
import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellContent,
	CellText,
	CellTextDate,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'
import { Text } from '@/components/ui/text'

import { useAuth } from '@/libs/hooks/use-auth'
import { toIdr } from '@/libs/utils/format'

import { itemOutletList } from '@/features/item/api'
import { ItemMasterTypeBadge } from '@/features/item/components/common'
import type { IItemOutlet } from '@/features/item/type'
import { useUnit } from '@/features/unit/hook'

const searchParamsSchema = z.object({
	q: z.string().optional(),
})

export const Route = createFileRoute('/_app/inventory/stock/')({
	validateSearch: searchParamsSchema,
	component: Component,
})

function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header breadcrumbs={[['/inventory/stock', 'Inventory']]} />
			<PageCard.Root>
				<PageCard.Header title="Daftar stok barang" />
				<ItemOutletTable />
			</PageCard.Root>
		</PageLayout.Root>
	)
}

function ItemOutletTable() {
	const navigate = Route.useNavigate()
	const { q } = Route.useSearch()
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(itemOutletList.qry({ outletId }))

	const columns = useMemo(getColumns, [])
	const { table } = useDataTable({
		state: { globalFilter: q },
		columns,
		data: data?.items ?? [],
		getRowId: (row) => row.id,
	})

	return (
		<Fragment>
			<PageCard.ContentSection className="flex justify-between py-2">
				<InputSearch
					defaultValue={q}
					onChange={(s) => navigate({ search: { q: s } })}
					placeholder="Cari item..."
				/>
			</PageCard.ContentSection>
			<DataTable
				table={table}
				columns={columns}
				isLoading={isLoading}
				onRowClick={(row) =>
					navigate({
						to: '/inventory/master/$id',
						params: { id: row.original.item.id },
					})
				}
			/>
		</Fragment>
	)
}

const ch = createColumnHelper<IItemOutlet>()

function getColumns() {
	return [
		ch.accessor('item.name', {
			header: 'Nama Item / Satuan',
			cell: ({ row }) => {
				const { name, unitId, type } = row.original.item
				const { byId } = useUnit()
				return (
					<CellContent
						style={{ maxWidth: 180 }}
						className="flex-col items-start py-2"
					>
						<Text className="max-w-full truncate text-nowrap" size="small">
							{name}
						</Text>
						<div className="flex items-center gap-1">
							<ItemMasterTypeBadge type={type} />
							<Text size="xsmall">Satuan: {byId(unitId)?.name}</Text>
						</div>
						{/* {name}&nbsp; (
						<span className="text-xs lowercase italic">
							{byId(unitId)?.base}
						</span>
						) */}
					</CellContent>
				)
			},
		}),
		ch.accessor('stockQty', {
			header: 'Stock Qty',
			cell: ({ row }) => {
				const { stockQty, item } = row.original
				const { byId } = useUnit()
				return <CellText text={`${stockQty} ${byId(item.unitId)?.base}`} />
			},
		}),
		ch.accessor('wacCost', {
			header: 'WAC Cost',
			cell: ({ row }) => <CellText text={toIdr(row.original.wacCost || 0)} />,
		}),
		ch.accessor('totalStockValue', {
			header: 'Stock Value',
			cell: ({ row }) => (
				<CellText text={toIdr(row.original.totalStockValue || 0)} />
			),
		}),
		ch.accessor('inventory.start', {
			header: 'Start',
			cell: ({ row }) => {
				const { inventory, item } = row.original
				const { byId } = useUnit()
				return (
					<CellText text={`${inventory.start} ${byId(item.unitId)?.base}`} />
				)
			},
		}),
		ch.accessor('inventory.adjustment', {
			header: 'Adjustment',
			cell: ({ row }) => {
				const { inventory, item } = row.original
				const { byId } = useUnit()
				return (
					<CellText
						text={`${inventory.adjustment} ${byId(item.unitId)?.base}`}
					/>
				)
			},
		}),
		ch.accessor('inventory.po', {
			header: 'PO',
			cell: ({ row }) => {
				const { inventory, item } = row.original
				const { byId } = useUnit()
				return <CellText text={`${inventory.po} ${byId(item.unitId)?.base}`} />
			},
		}),
		ch.accessor('inventory.sale', {
			header: 'Sale',
			cell: ({ row }) => {
				const { inventory, item } = row.original
				const { byId } = useUnit()
				return (
					<CellText text={`${inventory.sale} ${byId(item.unitId)?.base}`} />
				)
			},
		}),
		ch.accessor('inventory.end', {
			header: 'End',
			cell: ({ row }) => {
				const { inventory, item } = row.original
				const { byId } = useUnit()
				return <CellText text={`${inventory.end} ${byId(item.unitId)?.base}`} />
			},
		}),
		ch.accessor('syncAt', {
			header: 'Last Sync',
			cell: ({ row }) => {
				return <CellTextDate date={row.original.syncAt} />
			},
		}),
	]
}
